import React, { useEffect, useState } from "react";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Save, Trash2 } from "lucide-react";

const KINDS = [
  { key: "product",       label: "Products",       hint: "e.g. PR = P" },
  { key: "cabinet_type",  label: "Cabinet Types",  hint: "e.g. Kitchen = 1" },
  { key: "module",        label: "Modules",        hint: "e.g. Base Units = A, 3-Drawer = B" },
  { key: "door",          label: "Door Types",     hint: "e.g. GI PU Coated = GPU" },
  { key: "handle",        label: "Handles",        hint: "e.g. Gola Profile = 50" },
  { key: "material",      label: "Materials",      hint: "(optional — usually skipped from ID)" },
];

/** Item-ID Settings — only Super Admin & Production Design. Maps every master
 * entity's name to a short code that gets concatenated into the Item ID used
 * on the Production BOQ CSV. */
export default function ItemIdSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [keys, setKeys] = useState({});
  const [codes, setCodes] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [deletingKey, setDeletingKey] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quote/identifiers");
        setKeys(data.keys || {});
        setCodes(data.codes || {});
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);
  const save = async (kind, key, code) => {
    const marker = `${kind}::${key}`;
    setSavingKey(marker);
    try {
      await api.put("/quote/identifiers", { kind, key, code });
      setCodes((c) => ({ ...c, [kind]: { ...(c[kind] || {}), [key]: code } }));
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(""); }
  };
  const remove = async (kind, key) => {
    if (!window.confirm(`Delete the Item-ID code for "${key}"? Master data itself is not affected.`)) return;
    const marker = `${kind}::${key}`;
    setDeletingKey(marker);
    try {
      await api.delete(`/quote/identifiers/${encodeURIComponent(kind)}/${encodeURIComponent(key)}`);
      setCodes((c) => {
        const next = { ...(c[kind] || {}) };
        delete next[key];
        return { ...c, [kind]: next };
      });
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeletingKey(""); }
  };
  if (!user || !["super_admin", "production_design"].includes(user.role)) {
    return <DesktopLayout><div className="hk-card p-6 text-slate-600">Only Super Admin and Production Design can manage Item ID codes.</div></DesktopLayout>;
  }
  return (
    <DesktopLayout>
      <div className="mb-6">
        <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Settings</div>
        <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>Item ID codes</h1>
        <p className="text-sm text-slate-600 mt-1">
          Assign a short code to each master entity. The Production Design BOQ CSV joins these codes
          into a single Item ID per quote line (e.g. <span className="font-mono">P1ABGPU50_80_56_72HTHF</span>).
        </p>
      </div>
      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : (
        <div className="grid gap-6">
          {KINDS.map(({ key: kind, label, hint }) => (
            <section key={kind} className="hk-card p-5" data-testid={`itemid-section-${kind}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="hk-tiny-label mb-1">{label}</div>
                  <div className="text-[11px] text-slate-500 italic">{hint}</div>
                </div>
                <div className="text-[10px] font-black uppercase text-slate-400">{(keys[kind] || []).length} entries</div>
              </div>
              {(keys[kind] || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No master data — add entries under Quote Masters.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] font-black uppercase text-slate-500 border-b border-slate-200">
                      <th className="text-left py-2 pr-3">Name</th>
                      <th className="text-left py-2 pr-3 w-40">Code</th>
                      <th className="w-32"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(keys[kind] || []).map((k) => (
                      <IdentifierRow key={k} kind={kind} name={k}
                        code={(codes[kind] || {})[k] || ""}
                        saving={savingKey === `${kind}::${k}`}
                        deleting={deletingKey === `${kind}::${k}`}
                        onSave={(code) => save(kind, k, code)}
                        onDelete={() => remove(kind, k)} />
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          ))}
        </div>
      )}
    </DesktopLayout>
  );
}

function IdentifierRow({ kind, name, code, saving, deleting, onSave, onDelete }) {
  const [val, setVal] = useState(code);
  useEffect(() => { setVal(code); }, [code]);
  const changed = (val || "").trim() !== (code || "").trim();
  const hasCode = (code || "").trim().length > 0;
  return (
    <tr className="border-b border-slate-100" data-testid={`itemid-row-${kind}-${name}`}>
      <td className="py-2 pr-3 text-slate-800 truncate max-w-[300px]">{name}</td>
      <td className="py-2 pr-3">
        <input value={val} onChange={(e) => setVal(e.target.value)}
          placeholder="e.g. P"
          className="w-32 border border-slate-300 rounded-sm px-2 py-1 text-sm font-mono focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
          data-testid={`itemid-input-${kind}-${name}`} />
      </td>
      <td className="py-2">
        <div className="flex items-center gap-1">
          <button disabled={!changed || saving} onClick={() => onSave(val.trim())}
            className="inline-flex items-center gap-1 border border-slate-300 disabled:opacity-40 hover:bg-slate-50 text-slate-800 font-black uppercase text-[10px] px-2 py-1 rounded-sm"
            data-testid={`itemid-save-${kind}-${name}`}>
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            Save
          </button>
          <button disabled={!hasCode || deleting} onClick={onDelete}
            title="Delete code (master data stays)"
            className="inline-flex items-center gap-1 border border-slate-300 disabled:opacity-40 hover:bg-rose-50 hover:border-rose-300 hover:text-rose-700 text-slate-700 font-black uppercase text-[10px] px-2 py-1 rounded-sm"
            data-testid={`itemid-delete-${kind}-${name}`}>
            {deleting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
          </button>
        </div>
      </td>
    </tr>
  );
}
